import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { Shield } from "lucide-react";

const TRACK_RECORD = [
  "2010/13 | International Business Development Exec. - El Gouna, Egypt",
  "2013 | Top 10 People in Property & Tech - Property Week",
  "2014 | Shortlisted for Young Property Personality of the Year - Property Week",
  "2013 - 15 | Led Allied London's Workspace Strategy with £350m AUM",
  "Co-Founded Allied London's Co-work Platform - Hello Work [Now Allwork & Social]- 3 sites",
  "2015 - 17 | Head of Brand & PR Allied London - Launched Enterprise City",
  "2016 | Ran The Collective's Workspace Division - 5 sites",
  "2017 | Part of Founding Team of ISP Startup - Elevate - Minority Shareholder - Valued £20m+",
  "2017 | Launched The Content Group - Trading as Yolk Studios - Clients include Bumble, Ace & Tate, Pan McMillan, CBRE, Spinningfields ",
  "2019+ | Run CBRE's Space As A Service Operational Services - £90m AUM",
  "2021/22 | Internal Comms at Vitality Insurance",
  "2022/4 | Head of Brand at Blue Creast Wellness",
  "2024 | Launched Honey House Family Clubs",
];

export default function Loading() {
  const navigate = useNavigate();
  const [userType, setUserType] = useState<"investor" | "client" | null>(null);
  const [displayedText, setDisplayedText] = useState<string[]>([]);
  const [currentLineIndex, setCurrentLineIndex] = useState(0);
  const [currentCharIndex, setCurrentCharIndex] = useState(0);
  const [showContinue, setShowContinue] = useState(false);
  const [showStatus, setShowStatus] = useState(false);
  const [showConfidentiality, setShowConfidentiality] = useState(false);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const {
          data: { user },
          error: authError,
        } = await supabase.auth.getUser();

        if (authError || !user) {
          navigate("/");
          return;
        }

        const { data: userData, error: userError } = await supabase
          .from("users")
          .select("user_type")
          .eq("id", user.id)
          .single();

        if (userError || !userData) {
          console.error("Error fetching user type:", userError);
          navigate("/");
          return;
        }

        // If user is client, redirect to client loading
        if (userData.user_type === "client") {
          navigate("/loading-client");
          return;
        }

        setUserType(userData.user_type);
      } catch (error) {
        console.error("Error in loading screen:", error);
        navigate("/");
      }
    };

    fetchUser();

    // Show continue button after 3 seconds regardless of typing status
    const continueTimer = setTimeout(() => {
      setShowContinue(true);
    }, 3000);

    return () => clearTimeout(continueTimer);
  }, [navigate]);

  // Typewriter effect for track record
  useEffect(() => {
    if (currentLineIndex >= TRACK_RECORD.length) {
      // All lines typed, show status badge
      setShowStatus(true);
      return;
    }

    const currentLine = TRACK_RECORD[currentLineIndex];

    if (currentCharIndex < currentLine.length) {
      const timer = setTimeout(() => {
        setDisplayedText((prev) => {
          const newText = [...prev];
          if (!newText[currentLineIndex]) {
            newText[currentLineIndex] = "";
          }
          newText[currentLineIndex] = currentLine.substring(
            0,
            currentCharIndex + 1
          );
          return newText;
        });
        setCurrentCharIndex(currentCharIndex + 1);
      }, 30);

      return () => clearTimeout(timer);
    } else {
      // Move to next line after a brief pause
      const timer = setTimeout(() => {
        setCurrentLineIndex(currentLineIndex + 1);
        setCurrentCharIndex(0);
      }, 200);

      return () => clearTimeout(timer);
    }
  }, [currentLineIndex, currentCharIndex]);

  const handleContinueClick = () => {
    setShowConfidentiality(true);
  };

  const handleAcceptConfidentiality = () => {
    setShowConfidentiality(false);
    // Route to investor dashboard
    navigate("/investor/dashboard");
  };

  return (
    <div className="h-screen w-full bg-black flex flex-col md:flex-row">
      {/* Left Side - Loading */}
      <div className="w-full md:w-1/2 h-[45vh] md:h-screen flex flex-col items-center justify-center relative md:border-r-2 md:border-white/10 border-b-2 border-white/10 md:border-b-0">
        {/* Logo */}
        <div className="mb-8 text-center">
          <h1
            className="text-4xl md:text-5xl font-black tracking-[0.3em] text-white mb-2"
            style={{ fontFamily: "'Courier New', monospace" }}
          >
            TCGL
          </h1>
          <p
            className="text-sm text-gray-400 uppercase tracking-widest"
            style={{ fontFamily: "'Courier New', monospace" }}
          >
            Ventures
          </p>
        </div>

        {/* Spinner */}
        <div className="relative w-16 h-16">
          <div className="absolute inset-0 border-4 border-gray-800 rounded-full"></div>
          <div className="absolute inset-0 border-4 border-white border-t-transparent rounded-full animate-spin"></div>
        </div>

        {/* Continue Button - Above tagline */}
        <button
          onClick={handleContinueClick}
          className={`absolute bottom-[60px] md:bottom-[100px] left-5 md:left-12 px-4 md:px-6 py-2 md:py-3 border-2 border-white bg-white text-black text-xs md:text-sm font-bold uppercase tracking-wider transition-all hover:bg-transparent hover:text-white ${
            showContinue
              ? "opacity-100 translate-y-0"
              : "opacity-0 translate-y-2"
          }`}
          style={{
            fontFamily: "'Courier New', monospace",
            transition: "all 0.3s ease",
          }}
        >
          Continue →
        </button>

        {/* Bottom Tagline */}
        <div className="absolute bottom-8 md:bottom-12 left-0 right-0 text-center px-8">
          <p
            className="text-sm md:text-lg text-white/90 italic tracking-wide"
            style={{ fontFamily: "'Courier New', monospace" }}
          >
            Unless someone like you cares a whole awful lot, nothing is going to
            get better. It's not. - Dr Seuss
          </p>
        </div>
      </div>

      {/* Right Side - Track Record */}
      <div className="w-full md:w-1/2 h-[55vh] md:h-screen flex flex-col justify-center px-5 md:px-12 py-6 md:py-8 overflow-y-auto">
        {/* Header */}
        <div className="mb-6 md:mb-8">
          <h2
            className="text-2xl md:text-3xl font-black text-white mb-2 tracking-wide"
            style={{ fontFamily: "'Courier New', monospace" }}
          >
            OUR TRACK RECORD
          </h2>
          <div className="w-16 h-1 bg-white"></div>
        </div>

        {/* Code Engine / Typewriter Text */}
        <div className="space-y-3 md:space-y-4 text-sm">
          {displayedText.map((line, index) => (
            <div key={index} className="flex items-start gap-2 md:gap-3">
              <span
                className="text-green-400 mt-1 flex-shrink-0 text-xs md:text-sm"
                style={{ fontFamily: "'Courier New', monospace" }}
              >
                &gt;
              </span>
              <p
                className="text-gray-300 leading-relaxed text-xs md:text-sm"
                style={{ fontFamily: "'Courier New', monospace" }}
              >
                {line}
                {index === currentLineIndex &&
                  currentCharIndex < TRACK_RECORD[index].length && (
                    <span className="inline-block w-2 h-4 bg-green-400 ml-1 animate-pulse"></span>
                  )}
              </p>
            </div>
          ))}
        </div>

        {/* User Type Badge */}
        {userType && (
          <div
            className={`mt-8 md:mt-12 flex items-center gap-3 transition-opacity duration-500 ${
              showStatus ? "opacity-100" : "opacity-0"
            }`}
          >
            <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
            <span
              className="text-gray-500 text-xs uppercase tracking-widest"
              style={{ fontFamily: "'Courier New', monospace" }}
            >
              {userType} Portal Ready
            </span>
          </div>
        )}
      </div>

      {/* Confidentiality Modal */}
      {showConfidentiality && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black bg-opacity-80" />

          {/* Modal Content */}
          <div className="relative bg-white border-4 border-black shadow-[12px_12px_0px_0px_rgba(0,0,0,1)] max-w-md w-full">
            {/* Header */}
            <div className="flex items-center gap-3 p-6 border-b-4 border-black bg-yellow-50">
              <Shield className="w-8 h-8 text-black" />
              <h2 className="text-xl font-black text-black uppercase tracking-wide">
                Confidentiality Notice
              </h2>
            </div>

            {/* Body */}
            <div className="p-6">
              <p className="text-sm text-gray-700 mb-4 leading-relaxed">
                All information contained within this portal is{" "}
                <span className="font-bold text-black">
                  strictly private and confidential
                </span>{" "}
                and remains the copyright of The Content Group Limited.
              </p>

              <p className="text-sm text-gray-700 mb-6 leading-relaxed">
                By continuing, you acknowledge that you will not share,
                reproduce, or distribute any materials without prior written
                consent.
              </p>

              {/* Accept Button */}
              <button
                onClick={handleAcceptConfidentiality}
                className="w-full px-6 py-3 bg-black text-white font-bold text-sm uppercase tracking-wide border-2 border-black hover:bg-white hover:text-black transition-all"
              >
                I Understand & Accept
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
